// Unified schedule items for property (reservations + tasks + vendor visits) for Timeline and Calendar views

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useReservations } from "@/hooks/useReservations";
import { useCleaningTasks } from "@/hooks/useCleaningTasks";
import { usePropertyTaskChecklistAssignments } from "@/hooks/usePropertyTaskChecklistAssignments";
import { isTaskAssignedForSchedule, getTaskAssigneeLabelForSchedule } from "@/lib/taskAssigneeLabel";
import type { ScheduleItem, ScheduleItemStatus } from "@/types/schedule";
import type { CleaningTaskWithRelations } from "@/types/cleaning";

function isBlockedReservation(r: { reservation_type?: string; summary?: string | null }): boolean {
  return (
    r.reservation_type === "blocked" ||
    (!!r.summary && r.summary.toLowerCase().includes("not available"))
  );
}

export function useScheduleItems(propertyId: string | undefined) {
  const { data: reservations = [], isLoading: reservationsLoading } = useReservations(propertyId);
  const { data: cleaningTasks = [], isLoading: tasksLoading } = useCleaningTasks(propertyId);
  const taskIds = useMemo(
    () => (cleaningTasks as CleaningTaskWithRelations[]).map((t) => t.id),
    [cleaningTasks],
  );
  const { data: checklistAssignmentsByTaskId, isLoading: checklistAssignmentsLoading } =
    usePropertyTaskChecklistAssignments(taskIds);
  const { data: vendorVisits = [], isLoading: vendorVisitsLoading } = useQuery({
    queryKey: ["vendor-visits", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("vendor_visits")
        .select("id, property_id, maintenance_issue_id, title, start_at, end_at")
        .eq("property_id", propertyId)
        .order("start_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; property_id: string; maintenance_issue_id: string; title: string; start_at: string; end_at: string }>;
    },
    enabled: !!propertyId,
  });

  const items = useMemo((): ScheduleItem[] => {
    const list: ScheduleItem[] = [];
    const reservationIdsWithTask = new Set(
      (cleaningTasks as CleaningTaskWithRelations[]).map((t) => t.reservation_id).filter(Boolean)
    );

    (reservations as Array<Record<string, unknown>>).forEach((r) => {
      const id = r.id as string;
      const startDate = r.start_date as string;
      const endDate = r.end_date as string;
      const blocked = isBlockedReservation(r as { reservation_type?: string; summary?: string | null });
      const hasTask = reservationIdsWithTask.has(id);
      const status: ScheduleItemStatus = blocked ? "scheduled" : hasTask ? "scheduled" : "no_task";

      const start = startDate.includes("T") ? startDate : `${startDate}T00:00:00`;
      const end = endDate.includes("T") ? endDate : `${endDate}T23:59:59`;

      const sourceLabel = blocked
        ? null
        : (r.is_manual ? "Manual" : (r.source as string) || null);
      const title = blocked ? (r.summary as string) || "Blocked" : "Reserved";
      list.push({
        id: `res-${id}`,
        type: "reservation",
        start,
        end,
        title,
        status,
        source: (r.source as string) || null,
        sourceLabel: sourceLabel || null,
        relatedReservationId: null,
        reservationPayload: r,
        isBlocked: blocked,
      });
      if (!blocked && !hasTask) {
        list.push({
          id: `need-${id}`,
          type: "turnover_needed",
          start: endDate,
          end: endDate,
          title: "Turnover needed",
          status: "no_task",
          reservationPayload: r,
          sourceLabel: sourceLabel || null,
        });
      }
    });

    (cleaningTasks as CleaningTaskWithRelations[]).forEach((t) => {
      const dateStr = t.scheduled_date;
      const hasTime = t.scheduled_start_at && t.scheduled_end_at;
      const start = hasTime ? t.scheduled_start_at : (dateStr.includes("T") ? dateStr : `${dateStr}T09:00:00`);
      const end = hasTime ? t.scheduled_end_at : (dateStr.includes("T") ? dateStr : `${dateStr}T10:00:00`);
      const status = (t.status as ScheduleItemStatus) || "scheduled";
      const checklistName = (t.checklists as { name?: string } | null)?.name ?? null;
      const res = t.reservations as { check_out_time?: string | null; check_in_time?: string | null } | null;
      const outTime = (t as { override_check_out_time?: string | null }).override_check_out_time ?? res?.check_out_time ?? "11:00 AM";
      const inTime = (t as { override_check_in_time?: string | null }).override_check_in_time ?? res?.check_in_time ?? "4:00 PM";
      const timeRangeDisplay = `${outTime} → ${inTime}`;

      const title =
        t.reservation_id
          ? "Turnover"
          : ((t as { notes?: string | null }).notes || (t.checklists as { name?: string } | null)?.name || "Cleaning");

      const checklistRows = checklistAssignmentsByTaskId?.get(t.id);
      const isAssigned = isTaskAssignedForSchedule(
        t as { assigned_type?: string | null; cleaner_id?: string | null; assignee_user_id?: string | null },
        checklistRows,
      );
      const assignedToName = getTaskAssigneeLabelForSchedule(
        t as { assigned_type?: string | null; cleaner_id?: string | null; assignee_user_id?: string | null; coordinator_user_id?: string | null },
        checklistRows,
        {
          getCleanerName: () => null,
          getAssigneeUserName: () => null,
        },
      );

      list.push({
        id: `task-${t.id}`,
        type: "task",
        start,
        end,
        title,
        status,
        assignedTo: t.cleaner_id || null,
        assignedToName: assignedToName !== "Unassigned" ? assignedToName : null,
        relatedReservationId: t.reservation_id || null,
        taskSubtype: t.reservation_id ? "turnover" : "manual",
        taskPayload: t,
        checklistName: checklistName || null,
        timeRangeDisplay,
        isAssigned,
      });
    });

    (vendorVisits as Array<{ id: string; maintenance_issue_id: string; title: string; start_at: string; end_at: string }>).forEach((v) => {
      list.push({
        id: `vendor-visit-${v.id}`,
        type: "vendor_visit",
        start: v.start_at,
        end: v.end_at,
        title: v.title,
        status: "scheduled" as ScheduleItemStatus,
        vendorVisitPayload: {
          id: v.id,
          maintenance_issue_id: v.maintenance_issue_id,
          title: v.title,
          start_at: v.start_at,
          end_at: v.end_at,
        },
      });
    });

    list.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
    return list;
  }, [reservations, cleaningTasks, vendorVisits, checklistAssignmentsByTaskId]);

  return {
    items,
    reservations: reservations as Array<Record<string, unknown>>,
    cleaningTasks: cleaningTasks as CleaningTaskWithRelations[],
    isLoading: reservationsLoading || tasksLoading || vendorVisitsLoading || checklistAssignmentsLoading,
  };
}
